import { useEffect, useState } from 'react';
import { SlidersHorizontal, Grid3X3, List } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import ChatWidget from '../components/ChatWidget';
import { getProducts } from '../lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import useSEO from '../hooks/useSEO';

const ProductsPage = () => {
  useSEO({ title: 'All Products', description: 'Browse NestFlow space-saving storage products — organizers, bins, cable clips, and bundles for every room.' });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('featured');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getProducts();
        // Filter out-of-stock products
        setProducts(data.filter(p => p.in_stock));
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const categories = ['all', ...new Set(products.map(p => p.category))];

  const filteredProducts = products
    .filter(p => filter === 'all' || p.category === filter)
    .sort((a, b) => {
      switch (sort) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'featured':
        default:
          return b.is_featured - a.is_featured;
      }
    });

  return (
    <div className="min-h-screen bg-white" data-testid="products-page">
      <Header />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Page Header */}
          <div className="mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1A1A1C] font-['Manrope'] tracking-tight mb-4">
              All Products
            </h1>
            <p className="text-[#5C5C60] max-w-xl">
              Premium space-saving solutions designed for modern apartment living.
            </p>
          </div>

          {/* Filters Bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-[#E5E5E5]">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="w-5 h-5 text-[#5C5C60]" strokeWidth={1.5} />
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-[140px] bg-white border-[#E5E5E5] rounded-xl" data-testid="category-filter">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-[160px] bg-white border-[#E5E5E5] rounded-xl" data-testid="sort-filter">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                  <SelectItem value="newest">Newest</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-[#5C5C60]">
                {filteredProducts.length} products
              </span>
              <div className="flex border border-[#E5E5E5] rounded-lg overflow-hidden ml-4">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-[#1A1A1C] text-white' : 'bg-white text-[#5C5C60] hover:bg-[#F3F2EE]'}`}
                  data-testid="view-grid-btn"
                  aria-label="Grid view"
                >
                  <Grid3X3 className="w-5 h-5" strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-[#1A1A1C] text-white' : 'bg-white text-[#5C5C60] hover:bg-[#F3F2EE]'}`}
                  data-testid="view-list-btn"
                  aria-label="List view"
                >
                  <List className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className={`grid ${viewMode === 'grid' ? 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'} gap-6`}>
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                  <div className="aspect-square bg-[#F3F2EE] rounded-xl mb-4" />
                  <div className="h-4 bg-[#F3F2EE] rounded w-1/4 mb-2" />
                  <div className="h-6 bg-[#F3F2EE] rounded w-3/4 mb-2" />
                  <div className="h-4 bg-[#F3F2EE] rounded w-full" />
                </div>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#5C5C60]">No products found in this category.</p>
            </div>
          ) : (
            <div className={`grid ${viewMode === 'grid' ? 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'} gap-6`}>
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default ProductsPage;
